import React, { useEffect, useState } from "react";
import api, { fmtErr } from "../../lib/api";
import { fmtEUR, fmtDate } from "../../lib/format";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";

const FONTE = { openbanking: "PSD2", pdf_estratto_conto: "PDF estratto", pdf_lista_movimenti: "PDF lista", pdf_estratto_bpm: "PDF BPM", csv: "CSV" };

export default function DuplicatiPanel({ accountId, onChanged }) {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/bank/duplicati", { params: accountId ? { account_id: accountId } : {} }).then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, [accountId]);
  if (!d || d.gruppi.length === 0) return null;
  const unisci = async (g) => {
    setBusy(true);
    try { const r = await api.post("/bank/duplicati/unisci", { chiave: g.chiave }); toast.success(`${r.data.duplicati} doppioni rimossi`); load(); onChanged?.(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const tutti = async () => {
    if (!window.confirm(`Rimuovere ${d.totale_doppioni} doppioni? Per ogni movimento resta una sola copia (preferendo quella già riconciliata o da PSD2). Si può ripristinare dal filtro «Duplicati».`)) return;
    setBusy(true);
    try { const r = await api.post("/bank/duplicati/unisci-tutti", { account_id: accountId || undefined }); toast.success(`${r.data.duplicati} doppioni rimossi${r.data.conflitti ? ` · ${r.data.conflitti} gruppi da sistemare a mano (riconciliati due volte)` : ""}`); load(); onChanged?.(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Card className="p-4 border-amber-200 bg-amber-50/40" data-testid="bank-duplicati-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div><div className="text-sm font-semibold flex items-center gap-2"><Copy className="w-4 h-4 text-amber-600" /> Possibili movimenti doppi <Badge variant="outline">{d.totale_doppioni}</Badge></div>
          <p className="text-xs text-muted-foreground">Stesso conto, data e importo arrivati da fonti diverse (PSD2, estratto conto PDF, lista movimenti, CSV). I nuovi import vengono già bloccati; questi sono quelli storici.</p></div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setOpen(!open)} data-testid="bank-duplicati-toggle">{open ? "Nascondi" : `Vedi ${d.gruppi.length} gruppi`}</Button>
          <Button size="sm" onClick={tutti} disabled={busy} data-testid="bank-duplicati-tutti"><Check className="w-4 h-4 mr-1" />Rimuovi tutti i doppioni</Button>
        </div>
      </div>
      {open && <div className="mt-3 space-y-2">
        {d.gruppi.map((g) => (
          <div key={g.chiave} className="rounded border bg-white p-2 text-xs" data-testid="bank-duplicati-gruppo">
            <div className="flex items-center justify-between gap-2 mb-1"><div className="font-medium">{fmtDate(g.data)} · <span className={g.importo < 0 ? "text-destructive" : "text-emerald-700"}>{fmtEUR(g.importo)}</span> · {g.conto}</div>
              {g.conflitto ? <span className="text-amber-700">Riconciliato in entrambe le fonti (doppio in prima nota)</span> : null}<Button size="sm" variant="ghost" className="h-6" onClick={() => unisci(g)} disabled={busy} data-testid="bank-duplicati-unisci">Tieni uno, rimuovi {g.doppioni_stimati}</Button></div>
            {g.movimenti.map((m) => <div key={m.id} className={`flex items-center gap-2 py-0.5 ${m.id === g.tieni ? "font-medium" : "text-muted-foreground"}`}><Badge variant="outline" className="text-[10px]">{FONTE[m.fonte] || m.fonte}</Badge><span className="truncate">{m.descrizione}</span><span className="ml-auto whitespace-nowrap">{m.stato}{m.id === g.tieni ? " · tieni" : ""}</span></div>)}
          </div>
        ))}
      </div>}
    </Card>
  );
}
